import { PlayerError } from '@foundation-player/error-codes';
import type { PluginConfig as StreamFallbackPluginConfig } from '@foundation-player/plugin-stream-fallback';
import { PlayerType } from '../../builder-config';
import { TransformPluginConfigs } from './transform-plugin-configs';

/**
 * This function is used to evaluate the plugin configuration for the Stream Fallback Plugin.
 *
 * @remarks
 * The plugin is enabled by default for all clients.
 *
 * If the client only wants to play progressive streams, the plugin can be disabled, because it is not needed.
 * This is checked via the playerType, if the playerType is Native, the plugin is disabled.
 */
export const addStreamFallbackPlugin: TransformPluginConfigs = (partialBuilderConfig, pluginConfigs) => {
  const streamFallback = 'foundation.plugin.streamFallback';
  const { playerType, remoteConfig } = partialBuilderConfig;

  if (pluginConfigs[streamFallback]?.enabled === false) {
    return pluginConfigs;
  }

  if (!pluginConfigs[streamFallback]?.enabled && playerType === PlayerType.Native) {
    return pluginConfigs;
  }

  const nonFatalErrorCodes = [
    PlayerError.ATV_SCHEDULER_UNAVAILABLE_41700.code,
    PlayerError.ATV_SCHEDULER_REQUEST_FAILED_HTTP_STATUS_CODE_4XX_42700.code,
  ];

  const userConfig = pluginConfigs['foundation.plugin.streamFallback'];

  const config: StreamFallbackPluginConfig = {
    startup: {
      retryCurrentSource: userConfig?.startup?.retryCurrentSource ?? false,
      ignoreErrorCodes: userConfig?.startup?.ignoreErrorCodes ?? nonFatalErrorCodes,
      endSafetyIntervalInSeconds: 0,
      limitErrorCodes: userConfig?.startup?.limitErrorCodes ?? [],
      maxAttempts: userConfig?.startup?.maxAttempts ?? (remoteConfig.fallback?.startup?.enable === false ? 0 : -1),
    },
    inStream: {
      retryCurrentSource: userConfig?.inStream?.retryCurrentSource ?? true,
      ignoreErrorCodes: userConfig?.inStream?.ignoreErrorCodes ?? nonFatalErrorCodes,
      endSafetyIntervalInSeconds: userConfig?.inStream?.endSafetyIntervalInSeconds ?? 30,
      limitErrorCodes: userConfig?.inStream?.limitErrorCodes ?? [
        PlayerError.CLIENT_30000.code, // 30000 - e.g. on Tizen as a generic error when the player cannot play the stream
        PlayerError.VIDEOSOURCES_CONFIG_34200.code, // 34200 - e.g. on Tizen after an ad error occurs
        PlayerError.HDCP_REQUIREMENT_NOT_FULFILLED_12300.code, // 12300 - e.g. when you later plug into a dock and the stream requires HDCP
        PlayerError.FALLBACK_DRM_ERROR_10000.code, // 10000 - when a generic drm error is thrown, which we cannot determine
      ],
      maxAttempts: userConfig?.inStream?.maxAttempts ?? (remoteConfig.fallback?.inStream?.enable === false ? 0 : -1),
    },
  };

  const enabled = config.startup.maxAttempts !== 0 || config.inStream.maxAttempts !== 0;

  return {
    ...pluginConfigs,
    [streamFallback]: enabled
      ? {
          ...config,
          enabled,
        }
      : { enabled: false },
  };
};
